import { Button, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputAdornment, Radio, RadioGroup, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import type * as Immutable from 'immutable';
import { ChangeEvent, Dispatch, FocusEvent, SetStateAction, SyntheticEvent, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dinero from 'dinero.js';
import ProductOptionStore from '../../../AppData/_stores/ProductOptionStore';
import ProductStore from '../../../AppData/_stores/ProductStore';
import { UserProductDesignStore } from '../../../UserData/_stores/UserProductDesignStore';
import type ListingStore from '../../../UserData/_stores/Store/ListingStore';
import PriceRow from './PriceRow';
import PriceStore from '../../../UIData/_stores/PriceStore';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import { StoreListingVariantPrices, type StoreListingVariantPriceMap } from './Utils/StoreListingVariantPrices';
import buildStorePriceSections, { type StorePriceGroup } from './Utils/buildStorePriceSections';
import { MINIMUM_LISTING_PRICE_AMOUNT } from './Utils/StoreListingValidation';

export type Props = {
	price: PriceStore
	variantPrices: StoreListingVariantPriceMap
	disabledOptionIds: Immutable.List<number>
	onChangeListing: Dispatch<SetStateAction<ListingStore>>

	index?: number
	handleStep?: (step: number) => void
	disableButtons: () => void
	enableButtons: () => void
	labExit?: boolean
	product: ProductStore
	productDesign?: UserProductDesignStore
	massPresets?: boolean
	error?: boolean
	errorMessage?: string
}

const StyledTableHead = styled(TableHead)(({ theme }) => ({
	border: 0,
}))

const HeaderCell = styled(TableCell)(({ theme }) => ({
	backgroundColor: '#cbecf8',
	textTransform: 'none',
	fontSize: '1.05rem',
	color: theme.palette.secondary.main,
	borderRadius: 0,
	borderLeft: '1px solid transparent',
	borderRight: '1px solid transparent',
	borderBottom: 0,
	padding: theme.spacing(1, 2),

	'&.level1': {
		backgroundColor: '#addff2',
	},

	'&.level2': {
		backgroundColor: '#99cee2',
	}
}))

type ProfitType = 'fixed' | 'percentage'

function EditProductPrices(props: Props) {
	const [t, i18n] = useTranslation('account-v2')

	const currency = useAppSelector(state => state.get('userData').get('prefs').get('currency'))

	const [profitType, setProfitType] = useState<ProfitType>('percentage')
	const [profitFixed, setProfitFixed] = useState('10.00')
	const [profitPercentage, setProfitPercentage] = useState('25')

	const onProfitTypeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setProfitType(e.target.value as ProfitType), [])

	const onProfitPercentageChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setProfitPercentage(e.target.value)
	}, [])

	const onProfitFixedChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setProfitFixed(e.target.value)
	}, [])

	const availableOptionTypes = useMemo(() => {
		return props.massPresets ? props.product.get('availableOptionTypes') : props.productDesign?.get('availableOptionTypes');
	}, [props.massPresets, props.product, props.productDesign]);

	const disabledOptionIds = props.disabledOptionIds;

	const enabledOptionTypes = useMemo(() => {
		if (!availableOptionTypes || disabledOptionIds.count() === 0) {
			return availableOptionTypes;
		}

		const disabledOptionIdSet = new Set(disabledOptionIds.toArray());

		return availableOptionTypes.map(type => {
			return type.set('options', type.get('options').filter(option => {
				return !disabledOptionIdSet.has(option.get('id'));
			}));
		});
	}, [availableOptionTypes, disabledOptionIds]);

	// These lists decide whether pricing can stay on the base product or needs variant rows.
	const [optionTypesWithNoEmptyPrice, optionTypesWithOnlyOneEmptyPrice, variantsWithNoPrices] = useMemo(() => {
		let optionTypesWithNoEmptyPrice: number[] = [];
		let optionTypesWithOnlyOneEmptyPrice: number[] = [];
		let variantsWithNoPrices: number[] = [];

		if (!enabledOptionTypes) return [[], [], []];

		enabledOptionTypes.forEach((type) => {
			//Skip branding options
			if (['include-label'].includes(type.get('slug'))) {
				return;
			}

			let hasEmptyPrice = false;
			let hasPrices = new Set();

			let optionsByPVariants = new Map<number, Map<string, ProductOptionStore>>();

			// Product variants with matching prices can be collapsed out of the price table.
			type.get('options').forEach((option) => {
				if (option.get('price_dropship').get('amount') > 0) {
					hasPrices.add(option.get('slug'));
				} else {
					hasEmptyPrice = true;
				}

				if (!optionsByPVariants.has(option.get('id_product_variant'))) {
					optionsByPVariants.set(option.get('id_product_variant'), new Map<string, ProductOptionStore>());
				}

				optionsByPVariants.get(option.get('id_product_variant'))?.set(option.get('slug'), option);
			});

			//Compare options by variants
			optionsByPVariants.forEach((listOptions, pVariantId) => {
				let hasSamePrice = true;
				listOptions.forEach((option) => {
					optionsByPVariants.forEach((listOptions2) => {
						if (listOptions2.has(option.get('slug'))
							&& listOptions2.get(option.get('slug'))?.get('price_dropship').get('amount') != option.get('price_dropship').get('amount')) {
							hasSamePrice = false;
						}
					});
				});

				if (hasSamePrice) {
					variantsWithNoPrices.push(pVariantId);
				}
			});

			if (hasPrices.size === type.get('options').count()) {
				optionTypesWithNoEmptyPrice.push(type.get('id'));
			}

			if (type.get('options').count() > 1 && hasPrices.size === type.get('options').map((option) => option.get('slug')).toSet().size - 1) {
				optionTypesWithOnlyOneEmptyPrice.push(type.get('id'));
			}
		});

		return [optionTypesWithNoEmptyPrice, optionTypesWithOnlyOneEmptyPrice, variantsWithNoPrices];
	}, [enabledOptionTypes])

	const baseProductPrice = useMemo(() => {
		const listingPrice = props.price;

		return listingPrice.get('amount') > 0
			? listingPrice.toDinero()
			: props.product.get('price_retail').toDinero();
	}, [props.price, props.product])

	const customChoiceKeys = useMemo(() => {
		if (!availableOptionTypes) return new Set<string>();
		return new Set(StoreListingVariantPrices.normalizeOverrides(props.variantPrices, props.product, availableOptionTypes)
			.valueSeq().map(variantPrice => StoreListingVariantPrices.getChoiceKey(variantPrice)).toArray());
	}, [availableOptionTypes, props.product, props.variantPrices]);

	const defaultChoicePrices = useMemo(() => {
		const prices = new Map<string, PriceStore>();
		if (!availableOptionTypes) return prices;

		StoreListingVariantPrices.getDefaultPrices(props.product, availableOptionTypes, disabledOptionIds).forEach(variantPrice => {
			const key = StoreListingVariantPrices.getChoiceKey(variantPrice);
			const price = StoreListingVariantPrices.getAdjustedDefaultPrice(variantPrice, props.product, props.price);
			const current = prices.get(key);
			if (!current || price.get('amount') > current.get('amount')) prices.set(key, price);
		});
		return prices;
	}, [availableOptionTypes, disabledOptionIds, props.price, props.product]);

	const variantPriceGroups = useMemo(() => {
		let variantPriceGroups = new Map<string, StorePriceGroup>();

		if (!availableOptionTypes) return variantPriceGroups;

		const defaultVariantPrices = StoreListingVariantPrices.resolvePrices(
			StoreListingVariantPrices.getDefaultPrices(props.product, availableOptionTypes, disabledOptionIds),
			StoreListingVariantPrices.filterEnabled(
				StoreListingVariantPrices.normalizeOverrides(props.variantPrices, props.product, availableOptionTypes),
				disabledOptionIds,
			),
			props.product,
			props.price,
		);

		// One visible row can represent several backing variants with the same price slug.
		defaultVariantPrices.forEach((variantPrice, variantId) => {
			if (variantPriceGroups.has(variantPrice.get('slug'))) {
				variantPriceGroups.get(variantPrice.get('slug'))?.listVariantPrices.push(variantPrice);
				return;
			}

			variantPriceGroups.set(variantPrice.get('slug'), {
				name: variantPrice.get('name'),
				listVariantPrices: [variantPrice],
			});
		});

		return variantPriceGroups;
	}, [availableOptionTypes, disabledOptionIds, props.price, props.variantPrices, props.product])

	const priceSections = useMemo(() => buildStorePriceSections(
		variantPriceGroups, availableOptionTypes, i18n.resolvedLanguage ?? i18n.language,
	), [availableOptionTypes, i18n.language, i18n.resolvedLanguage, variantPriceGroups]);

	const onBaseProductPriceChange = useCallback((price: Dinero.Dinero) => {
		// Default option prices follow the base through resolvePrices; saved overrides stay fixed.
		props.onChangeListing(listing => listing.set('price', PriceStore.fromDinero(price)))
	}, [props.onChangeListing])

	const onApplyFixed = useCallback(() => {
		let parsedInput = parseFloat(profitFixed);
		parsedInput = isNaN(parsedInput) ? 0 : Math.round(parsedInput * 100);

		const fixedProfit = Dinero({ amount: parsedInput, currency: currency })
		setProfitFixed(fixedProfit.toFormat('0.00'))

		props.onChangeListing(listing => {
			let tmpListing = listing;

			//Update base price
			const costPrice = props.product.get('price_dropship').toDinero()
			const newPrice = costPrice.add(fixedProfit)
			tmpListing = tmpListing.set('price', PriceStore.fromDinero(newPrice));

			//Update variant prices
			// Keep one draft price for each choice; disabled combinations are filtered when saving.
			let variantPrices = tmpListing.get('variant_prices');
			if (availableOptionTypes) {
				if (priceSections.length === 0 && variantPrices.count() > 0) {
					// Keep saved price-neutral aliases in sync when only the base row is visible.
					variantPrices = StoreListingVariantPrices.setChoicePrice(variantPrices,
						StoreListingVariantPrices.getDefaultPrices(props.product, availableOptionTypes, disabledOptionIds).valueSeq().toArray(),
						PriceStore.fromDinero(newPrice), { product: props.product, availableOptionTypes });
				}
				priceSections.forEach(section => section.rows.forEach(row => {
					const costPrice = row.costPrice.toDinero();
					const newPrice = costPrice.add(fixedProfit);
					variantPrices = StoreListingVariantPrices.setChoicePrice(variantPrices, row.group.listVariantPrices, PriceStore.fromDinero(newPrice), { product: props.product, availableOptionTypes });
				}));
			}
			tmpListing = tmpListing.set('variant_prices', variantPrices);

			return tmpListing;
		})
	}, [availableOptionTypes, disabledOptionIds, profitFixed, currency, props.onChangeListing, props.product, priceSections])

	const onApplyPercentage = useCallback(() => {
		let parsedInput = parseFloat(profitPercentage);
		parsedInput = isNaN(parsedInput) ? 0 : Math.round(parsedInput * 100) / 10000; //Keep 2 decimals only
		parsedInput = Math.max(0, Math.min(0.9999, parsedInput)) //Max it out between 0 and 1

		setProfitPercentage((parsedInput * 100).toFixed(2))

		const inversedPercentage = 1 - parsedInput;

		props.onChangeListing(listing => {
			let tmpListing = listing;

			//Update base price
			const costPrice = props.product.get('price_dropship').toDinero()
			const newPrice = inversedPercentage > 0 ? costPrice.divide(inversedPercentage) : costPrice;
			tmpListing = tmpListing.set('price', PriceStore.fromDinero(newPrice));

			//Update variant prices
			// Keep one draft price for each choice; disabled combinations are filtered when saving.
			let variantPrices = tmpListing.get('variant_prices');
			if (availableOptionTypes) {
				if (priceSections.length === 0 && variantPrices.count() > 0) {
					// Keep saved price-neutral aliases in sync when only the base row is visible.
					variantPrices = StoreListingVariantPrices.setChoicePrice(variantPrices,
						StoreListingVariantPrices.getDefaultPrices(props.product, availableOptionTypes, disabledOptionIds).valueSeq().toArray(),
						PriceStore.fromDinero(newPrice), { product: props.product, availableOptionTypes });
				}
				priceSections.forEach(section => section.rows.forEach(row => {
					const costPrice = row.costPrice.toDinero();
					const newPrice = inversedPercentage > 0 ? costPrice.divide(inversedPercentage) : costPrice;
					variantPrices = StoreListingVariantPrices.setChoicePrice(variantPrices, row.group.listVariantPrices, PriceStore.fromDinero(newPrice), { product: props.product, availableOptionTypes });
				}));
			}
			tmpListing = tmpListing.set('variant_prices', variantPrices);

			return tmpListing;
		})
	}, [availableOptionTypes, disabledOptionIds, profitPercentage, props.onChangeListing, props.product, priceSections])

	return <>
		<Stack spacing={4}>
			<Stack spacing={1}>
				<Typography variant="h3">{t('Edit Pricing')}</Typography>

				<Typography variant="body1" sx={{ mb: 2 }}>{t('Edit pricing for the various sizes, colors and options.')}</Typography>
			</Stack>

			<Stack spacing={1}>
				<FormControl>
					<FormLabel>{t('Apply a net profit')}</FormLabel>
					<RadioGroup
						value={profitType}
						onChange={onProfitTypeChange}
						row
					>
						<FormControlLabel value="percentage" control={<Radio />} label={t('Percentage')} />
						<FormControlLabel value="fixed" control={<Radio />} label={t('Fixed amount')} />
					</RadioGroup>
				</FormControl>

				<Stack direction="row" spacing={1} alignItems="center">
					<TextField
						type="number"
						inputProps={{
							min: 0,
							step: 0.01,
						}}
						size="small"
						sx={{
							width: '180px',
							'& .MuiInputBase-root': {
								alignItems: 'stretch',
							},
						}}
						InputProps={{
							startAdornment: <InputAdornment position="start">{profitType === 'percentage' ? '%' : '$'}</InputAdornment>,
							endAdornment: <InputAdornment
								position="end"
								sx={{
									alignSelf: 'stretch',
									height: 'auto',
									maxHeight: 'none',
									m: 0,
								}}
							>
								<Button
									variant="outlined"
									color="secondary"
									size="medium"
									onClick={profitType === 'percentage' ? onApplyPercentage : onApplyFixed}
									sx={{
										alignSelf: 'stretch',
										backgroundColor: '#fff',
										borderRadius: '30px',
										height: 'auto',
										minHeight: 0,
									}}
								>{t('Apply')}</Button>
							</InputAdornment>
						}}
						value={profitType === 'percentage' ? profitPercentage : profitFixed}
						onChange={profitType === 'percentage' ? onProfitPercentageChange : onProfitFixedChange}
					/>
				</Stack>
			</Stack>

			<Stack spacing={1}>
				{props.error ? <FormHelperText error sx={{ mt: 0 }}>{props.errorMessage ?? t('Please add pricing to continue.')}</FormHelperText> : null}
				<TableContainer>
					<Table sx={{ minWidth: 600 }}>
						<StyledTableHead>
							<TableRow>
								<HeaderCell component="th">{t('Variant')}</HeaderCell>
								<HeaderCell component="th" className="level1">{t('Your Price')}</HeaderCell>
								<HeaderCell component="th" className="level1">{t('Cost')}</HeaderCell>
								<HeaderCell component="th" className="level1">{t('Your Profit')}</HeaderCell>
							</TableRow>
						</StyledTableHead>
						<TableBody>
							{props.product.get('price_dropship').get('amount') > 0
								&& optionTypesWithNoEmptyPrice.length === 0
								&& optionTypesWithOnlyOneEmptyPrice.length === 0
								&& <PriceRow
									name={t('Base Product')}
									price={baseProductPrice}
									defaultPrice={props.product.get('price_retail').toDinero()}
									isCustom={!baseProductPrice.equalsTo(props.product.get('price_retail').toDinero())}
									costPrice={props.product.get('price_dropship').toDinero()}
									onPriceChange={onBaseProductPriceChange}
									onReset={() => onBaseProductPriceChange(props.product.get('price_retail').toDinero())}
									minimumPriceAmount={MINIMUM_LISTING_PRICE_AMOUNT}
								/>
							}
						</TableBody>
						{priceSections.map(section => <TableBody key={section.key}>
							{section.name ? <TableRow>
								<TableCell component="th" scope="rowgroup" colSpan={4} sx={{ bgcolor: 'grey.100', fontWeight: 600, py: 1 }}>
									{section.name}
								</TableCell>
							</TableRow> : null}
							{section.rows.map(row => {
								const variantGroup = row.group;

								return <PriceRow
									key={row.key}
									name={row.name}
									description={row.mixedCosts ? t('Highest cost shown.') : undefined}
									price={row.price.toDinero()}
									defaultPrice={defaultChoicePrices.get(row.key)?.toDinero() ?? row.price.toDinero()}
									isCustom={customChoiceKeys.has(row.key)}
									costPrice={row.costPrice.toDinero()}
									minimumPriceAmount={MINIMUM_LISTING_PRICE_AMOUNT}
									onReset={() => {
										if (!availableOptionTypes) return;
										props.onChangeListing(listing => listing.set('variant_prices', StoreListingVariantPrices.resetChoicePrice(
											listing.get('variant_prices'), variantGroup.listVariantPrices, { product: props.product, availableOptionTypes },
										)));
									}}
									onPriceChange={(price: Dinero.Dinero) => {
										if (!availableOptionTypes) return;
										props.onChangeListing(listing => listing.set('variant_prices', StoreListingVariantPrices.setChoicePrice(
											listing.get('variant_prices'), variantGroup.listVariantPrices, PriceStore.fromDinero(price), { product: props.product, availableOptionTypes },
										)));
									}}
								/>
							})}
						</TableBody>)}
					</Table>
				</TableContainer>
			</Stack>
		</Stack>
	</>
}

export default memo(EditProductPrices);
